import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

/**
 * App-level "Link copied · Post to feed →" toast.
 *
 * Share buttons live inside odds tables whose rows remount on every poll,
 * so any toast state held in the row dies within seconds (and anchored
 * overlays sit in table stacking contexts that swallow clicks). This host
 * mounts once at the app root and is triggered imperatively via
 * {@link promptPostToFeed}, so it survives row churn and always receives
 * taps. Clicking it deep-links to the feed composer with the share link
 * pre-filled (the composer attaches the bet-card preview automatically).
 */

const PROMPT_EVENT = "bb:post-to-feed";
const TOAST_MS = 10000;

/** Show the toast for a just-copied bet share URL. */
export const promptPostToFeed = (url: string): void => {
  window.dispatchEvent(new CustomEvent(PROMPT_EVENT, { detail: { url } }));
};

const PostToFeedToast: React.FC = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      const promptUrl = (e as CustomEvent).detail?.url;
      if (typeof promptUrl !== "string" || !promptUrl) return;
      setUrl(promptUrl);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setUrl(null), TOAST_MS);
    };
    window.addEventListener(PROMPT_EVENT, onPrompt);
    return () => {
      window.removeEventListener(PROMPT_EVENT, onPrompt);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  if (!url) return null;

  return createPortal(
    <button
      type="button"
      onClick={() => {
        setUrl(null);
        navigate(`/feed?compose=${encodeURIComponent(url)}`);
      }}
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        /* Clear of the mobile bottom bar (zero-height on desktop) and in easy
         * thumb reach on phones. */
        bottom:
          "calc(var(--bb-bottomnav-h, 0px) + env(safe-area-inset-bottom, 0px) + 26px)",
        zIndex: 10050,
        display: "flex",
        alignItems: "center",
        gap: "9px",
        backgroundColor: "#0A1E3B",
        border: "1px solid rgba(148, 194, 220, 0.45)",
        borderRadius: "999px",
        padding: "10px 18px",
        fontSize: "13px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        cursor: "pointer",
        boxShadow: "0 14px 36px -10px rgba(2, 8, 20, 0.9)",
      }}
    >
      <span style={{ color: "#94c2dc" }}>Link copied</span>
      <span style={{ color: "rgba(148, 194, 220, 0.4)" }}>·</span>
      <span style={{ color: "#ffffff" }}>Post to feed →</span>
    </button>,
    document.body
  );
};

export default PostToFeedToast;
